/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {useCallback, useEffect, useState} from 'react'
import {rebuildPathWithParams, removeQueryParamsFromPath} from '../utils/url'
import {useHistory, useLocation} from 'react-router-dom'
import {useCommerceAPI} from '../commerce-api/contexts'
import {isError} from '../commerce-api/utils'
import {useVariant} from './use-variant'
import {useToast} from './use-toast'
import {useIntl} from 'react-intl'
import {API_ERROR_MESSAGE} from '../constants'
import {useColorVariation} from './use-variation-attributes'
import {unifyProduct} from './use-unified-product'
import useEffectEvent from './use-effect-event'

/**
 * This hooks is responsible for fetching a product detail based on the variation selection
 * and managing the variation params on the url when the modal is open/close
 * @param initialProduct - the initial product when the modal is first open
 * @returns object
 */
export const useProductViewModal = (
    initialProduct,
    removeRestrictedVariantsAndAttributes = false,
    isProductPartOfSet = false
) => {
    const location = useLocation()
    const api = useCommerceAPI()
    const history = useHistory()
    const intl = useIntl()
    const [product, setProduct] = useState(
        removeRestrictedVariantsAndAttributes
            ? removeRestrictedVariantsAndAttributes(initialProduct)
            : initialProduct
    )
    const [isFetching, setIsFetching] = useState(false)
    const toast = useToast()
    const variant = useVariant(product, isProductPartOfSet)
    const colorVariation = useColorVariation(product, isProductPartOfSet)

    const cleanUpVariantParams = useCallback(() => {
        const paramToRemove = [...(product?.variationAttributes?.map(({id}) => id) || []), 'pid']
        const updatedParams = removeQueryParamsFromPath(`${location.search}`, paramToRemove)

        history.replace({search: updatedParams})
    }, [history, location.search, product?.variationAttributes])

    const preselectVariantParams = useCallback(() => {
        cleanUpVariantParams()

        // set first orderable variant as default one
        const {value} =
            colorVariation?.values?.find(({orderable}) => orderable) ||
            colorVariation?.values?.[0] ||
            {}

        const initialUrl = rebuildPathWithParams(`${location.pathname}${location.search}`, {
            ...(value && {color: value}),
            pid: product?.id
        })
        history.replace(initialUrl)
    }, [
        cleanUpVariantParams,
        colorVariation?.values,
        history,
        location.pathname,
        location.search,
        product?.id
    ])

    const onInitialProductChange = useEffectEvent((initialProduct) => {
        setProduct(
            removeRestrictedVariantsAndAttributes
                ? removeRestrictedVariantsAndAttributes(initialProduct)
                : initialProduct
        )
    })
    const onCleanupVariantParams = useEffectEvent(() => {
        cleanUpVariantParams()
    })
    useEffect(() => {
        // when initialProduct changes set the new product
        onInitialProductChange(initialProduct)

        if (!isProductPartOfSet) {
            return () => {
                onCleanupVariantParams()
            }
        }
    }, [initialProduct, isProductPartOfSet, onCleanupVariantParams, onInitialProductChange])

    const onProductPartOfSetChanged = useEffectEvent((isProductPartOfSet) => {
        if (isProductPartOfSet) return

        // preselect default params in case there are variant params not related to current product
        preselectVariantParams()
    })
    useEffect(() => {
        onProductPartOfSetChanged(isProductPartOfSet)
    }, [isProductPartOfSet, onProductPartOfSetChanged])

    const onVariantOrPartOfSetChanged = useEffectEvent((variant, isProductPartOfSet) => {
        // getting product detail based on variant selection for stockLevel
        const getProductDetailByVariant = async () => {
            // Fetch the product detail when the user select different variant
            if (variant && variant.productId !== product?.id) {
                setIsFetching(true)
                const variantProduct = await api.shopperProducts.getProduct({
                    parameters: {
                        id: variant.productId,
                        allImages: true
                    }
                })
                const masterProduct = initialProduct?.type?.master
                    ? initialProduct
                    : await api.shopperProducts.getProduct({
                          parameters: {
                              id: product.master.masterId
                          }
                      })
                if (isError(variantProduct)) {
                    setIsFetching(false)
                    toast({
                        title: intl.formatMessage(API_ERROR_MESSAGE),
                        status: 'error'
                    })
                    throw new Error(variantProduct)
                } else if (isError(masterProduct)) {
                    setIsFetching(false)
                    toast({
                        title: intl.formatMessage(API_ERROR_MESSAGE),
                        status: 'error'
                    })
                    throw new Error(masterProduct)
                }

                const unifiedProduct = unifyProduct(masterProduct, variantProduct)
                setProduct(
                    removeRestrictedVariantsAndAttributes
                        ? removeRestrictedVariantsAndAttributes(unifiedProduct)
                        : unifiedProduct
                )
                setIsFetching(false)
            }
        }

        if (variant && !isProductPartOfSet) {
            const {variationValues} = variant
            // update the url with the new product id and variation values when the variant changes
            const updatedUrl = rebuildPathWithParams(`${location.pathname}${location.search}`, {
                ...variationValues,
                pid: variant.productId
            })
            history.replace(updatedUrl)
        }

        getProductDetailByVariant()
    })
    useEffect(() => {
        onVariantOrPartOfSetChanged(variant, isProductPartOfSet)
    }, [variant, isProductPartOfSet, onVariantOrPartOfSetChanged])

    return {
        product,
        variant,
        isFetching
    }
}
